import React, {useCallback, useMemo, useRef} from 'react';
import {Box, Unstable_Grid2 as Grid} from '@mui/material';
import SuggestiveSellingCompactCard from './suggestive-selling-compact-card';
import {useFixedRowVirtualizer} from '../../../hooks/use-row-virtualizer';
import {SUGGESTIVE_SELLING_VIRTUALIZE_THRESHOLD} from '../constants';
import {getSuggestiveSellingGridLayout} from '../utils/get-suggestive-selling-grid-layout';
import {getPartQuantity, isPartSelected} from '../utils/suggestive-selling-staging';

function chunkArray(items, size) {
    const rows = [];
    for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size));
    }
    return rows;
}

const SuggestiveSellingProductGrid = ({
                                          parts,
                                          staging,
                                          onQuantityChange,
                                          onSelectionChange,
                                          disabled,
                                      }) => {
    const scrollRef = useRef(null);
    const getScrollParent = useCallback(() => scrollRef.current, []);

    const layout = useMemo(() => getSuggestiveSellingGridLayout(parts.length), [parts.length]);

    const rows = useMemo(
        () => chunkArray(parts, layout.columnsPerRow),
        [layout.columnsPerRow, parts],
    );

    const useVirtual = parts.length > SUGGESTIVE_SELLING_VIRTUALIZE_THRESHOLD;

    const virtualizer = useFixedRowVirtualizer({
        data: rows,
        rowHeight: layout.rowHeight,
        overscan: 2,
        getScrollParent,
        getItemKey: (row, index) => row.map((p) => p.partId).join('-') || index,
    });

    const renderRow = useCallback((rowParts) => (
        <Grid container spacing={layout.spacing} sx={{mb: layout.spacing}}>
            {
                rowParts.map((part) => (
                    <Grid key={part.partId} {...layout.gridItemProps}>
                        <SuggestiveSellingCompactCard
                            part={part}
                            selected={isPartSelected(staging, part.partId)}
                            quantity={getPartQuantity(staging, part.partId)}
                            onQuantityChange={onQuantityChange}
                            onSelectionChange={onSelectionChange}
                            disabled={disabled}
                            minHeight={layout.cardMinHeight}
                            descriptionLineClamp={layout.descriptionLineClamp}
                            density={layout.density}
                        />
                    </Grid>
                ))
            }
            {
                rowParts.length < layout.columnsPerRow
                && Array.from({length: layout.columnsPerRow - rowParts.length}).map((_, index) => (
                    <Grid key={`pad-${index}`} {...layout.gridItemProps}/>
                ))
            }
        </Grid>
    ), [disabled, layout, onQuantityChange, onSelectionChange, staging]);

    if (!useVirtual) {
        return (
            <Box>
                {rows.map((rowParts, index) => (
                    <React.Fragment key={rowParts.map((p) => p.partId).join('-') || index}>
                        {renderRow(rowParts)}
                    </React.Fragment>
                ))}
            </Box>
        );
    }

    return (
        <Box
            ref={scrollRef}
            sx={{
                maxHeight: 420,
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            <Box sx={{height: virtualizer.topSpacerHeight}}/>
            {virtualizer.items.map((rowParts, visibleIndex) => (
                <Box key={virtualizer.keyFor(visibleIndex)} sx={{height: layout.rowHeight}}>
                    {renderRow(rowParts)}
                </Box>
            ))}
            <Box sx={{height: virtualizer.bottomSpacerHeight}}/>
        </Box>
    );
};

export default SuggestiveSellingProductGrid;
